import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import {
  Button,
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@erp/ui';
import { Bell, Check, Trash2, ShoppingBag, ShoppingCart, Users, AlertTriangle, Package, BookCheck, FileText, Volume2, VolumeX } from '@/components/icons';
import {
  useAdminNotifications, useUnreadNotificationCount,
  useMarkNotificationRead, useMarkAllNotificationsRead,
  useDeleteNotification, useEnsureCheckDraftReminders,
  useEnsureLowStockNotifications,
  useStoreSettings,
} from '@/hooks/use-storefront';
import type { AdminNotification, NotificationKind } from '@erp/data-access/interfaces';
import { playCashSound, playDing, playTest, isMuted, setMuted, setCustomSounds } from './notification-sounds';

const KIND_ICONS: Record<NotificationKind, React.ReactElement> = {
  order_new: <ShoppingBag size={16} />,
  order_status: <ShoppingBag size={16} />,
  customer_new: <Users size={16} />,
  customer_blocked: <AlertTriangle size={16} />,
  abandoned_cart: <ShoppingBag size={16} />,
  product_low_stock: <Package size={16} />,
  check_due: <BookCheck size={16} />,
  draft_due: <FileText size={16} />,
  system: <Bell size={16} />,
};

const KIND_COLORS: Record<NotificationKind, string> = {
  order_new: 'bg-emerald-500',
  order_status: 'bg-blue-500',
  customer_new: 'bg-violet-500',
  customer_blocked: 'bg-red-500',
  abandoned_cart: 'bg-amber-500',
  product_low_stock: 'bg-orange-500',
  check_due: 'bg-sky-500',
  draft_due: 'bg-indigo-500',
  system: 'bg-gray-500',
};

const ORDER_KINDS: NotificationKind[] = [
  'order_new', 'order_status', 'customer_new', 'customer_blocked', 'abandoned_cart',
];
const REMINDER_KINDS: NotificationKind[] = [
  'check_due', 'draft_due', 'product_low_stock', 'system',
];

/** Fire an OS-level toast so a background tab still alerts the user.
 *  Permission must have been granted beforehand — the user triggers that
 *  via the bell's bell-with-dot prompt. */
function showBrowserNotification(n: AdminNotification): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    const notif = new Notification(n.title, {
      body: n.message ?? '',
      tag: n.id,
      icon: '/favicon.ico',
    });
    notif.onclick = () => {
      window.focus();
      if (n.linkPath) window.location.assign(n.linkPath);
      notif.close();
    };
  } catch { /* some browsers throw if page not focused — ignore */ }
}

interface DropdownProps {
  kinds: NotificationKind[];
  icon: React.ReactNode;
  ariaLabel: string;
  soundKind: 'order' | 'reminder';
}

function NotificationsDropdown({ kinds, icon, ariaLabel, soundKind }: DropdownProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [muted, setMutedState] = useState(() => isMuted());
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  });
  const { data: unreadCount = 0 } = useUnreadNotificationCount({ kinds });
  const { data: notifications = [], isFetched } = useAdminNotifications({ limit: 15, kinds });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  // Play a sound + fire OS notification when a brand-new item lands.
  // Baseline ONLY after the first real fetch so we don't chime for the
  // existing feed on page load.
  const seenIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!isFetched) return;
    if (seenIdsRef.current == null) {
      seenIdsRef.current = new Set(notifications.map((n) => n.id));
      return;
    }
    const fresh = notifications.filter((n) => !seenIdsRef.current!.has(n.id) && !n.isRead);
    if (fresh.length > 0) {
      if (soundKind === 'order') playCashSound();
      else playDing();
      for (const n of fresh) showBrowserNotification(n);
    }
    for (const n of notifications) seenIdsRef.current!.add(n.id);
  }, [notifications, isFetched, soundKind]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) playTest(); // unmuting → let the user verify
  };

  const requestPermission = () => {
    if (typeof Notification === 'undefined') return;
    Notification.requestPermission().then((p) => setPermission(p)).catch(() => { /* noop */ });
  };

  const del = useDeleteNotification();

  const handleClick = (n: AdminNotification) => {
    if (!n.isRead) markRead.mutate(n.id);
    if (n.linkPath) {
      setOpen(false);
      navigate(n.linkPath);
    }
  };

  const titleKey = soundKind === 'order' ? 'notifications.ordersTitle' : 'notifications.remindersTitle';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-9 md:size-8" aria-label={ariaLabel}>
          {icon}
          {unreadCount > 0 && (
            <span className="absolute -top-2 -end-3 z-10 h-4 min-w-4 rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white ring-2 ring-background flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0 flex flex-col max-h-[520px] overflow-hidden"
      >
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b gap-2 bg-popover">
          <div className="min-w-0">
            <p className="font-semibold text-sm">{t(titleKey)}</p>
            {unreadCount > 0 && (
              <p className="text-xs text-muted-foreground">{t('notifications.unreadCount', { count: unreadCount })}</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={toggleMute}
              aria-label={muted ? t('notifications.unmute') : t('notifications.mute')}
              title={muted ? t('notifications.unmute') : t('notifications.mute')}
              className="text-muted-foreground"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </Button>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => markAll.mutate()} className="text-xs">
                <Check size={12} className="me-1" />
                {t('notifications.markAllRead')}
              </Button>
            )}
          </div>
        </div>

        {permission === 'default' && (
          <button
            type="button"
            onClick={requestPermission}
            className="shrink-0 w-full px-4 py-2 text-xs bg-primary/5 hover:bg-primary/10 text-primary text-start border-b transition-colors"
          >
            {t('notifications.enableBrowser')}
          </button>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <Bell size={28} className="mx-auto opacity-30 mb-2" />
              {t('notifications.empty')}
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onClick={() => handleClick(n)}
                  onDelete={() => del.mutate(n.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t p-2 flex items-center justify-center bg-popover">
          <Link
            to="/storefront/notifications"
            onClick={() => setOpen(false)}
            className="text-xs font-semibold text-primary hover:underline"
          >
            {t('notifications.viewAll')}
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function NotificationBell() {
  const { t } = useTranslation();
  const { data: storeSettings } = useStoreSettings();
  useEnsureCheckDraftReminders();
  useEnsureLowStockNotifications();

  // Push custom sound URLs into the sound module whenever settings change.
  // Mounted once here since both dropdowns share the same sound module.
  useEffect(() => {
    setCustomSounds({
      orderUrl: storeSettings?.notificationSoundOrderUrl ?? null,
      reminderUrl: storeSettings?.notificationSoundReminderUrl ?? null,
    });
  }, [storeSettings?.notificationSoundOrderUrl, storeSettings?.notificationSoundReminderUrl]);

  return (
    <NotificationsDropdown
      kinds={REMINDER_KINDS}
      icon={<Bell size={18} />}
      ariaLabel={t('notifications.remindersTitle')}
      soundKind="reminder"
    />
  );
}

export function OrderNotificationsBell() {
  const { t } = useTranslation();
  return (
    <NotificationsDropdown
      kinds={ORDER_KINDS}
      icon={<ShoppingCart size={18} />}
      ariaLabel={t('notifications.ordersTitle')}
      soundKind="order"
    />
  );
}

function NotificationRow({ notification, onClick, onDelete }: {
  notification: AdminNotification;
  onClick: () => void;
  onDelete: () => void;
}) {
  const timeAgo = formatRelative(notification.createdAt);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`group flex items-start gap-3 p-3 cursor-pointer hover:bg-accent transition-colors ${notification.isRead ? '' : 'bg-primary/5'}`}
    >
      <div className={`shrink-0 size-8 rounded-full text-white flex items-center justify-center ${KIND_COLORS[notification.kind]}`}>
        {KIND_ICONS[notification.kind] ?? <Bell size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className={`text-sm ${notification.isRead ? 'font-medium' : 'font-semibold'} truncate`}>
            {notification.title}
          </p>
          {!notification.isRead && <span className="size-2 rounded-full bg-primary shrink-0 mt-1" aria-label="Unread" />}
        </div>
        {notification.message && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{notification.message}</p>
        )}
        <p className="text-[10px] text-muted-foreground mt-1">{timeAgo}</p>
      </div>
      <Button
        variant="ghost"
        size="icon-xs"
        className="opacity-0 group-hover:opacity-100 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        aria-label="Delete"
      >
        <Trash2 size={12} />
      </Button>
    </div>
  );
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'À l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)} j`;
  return new Date(iso).toLocaleDateString('fr-TN');
}
